import React, { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
    Database, Search, RefreshCw, Layers, FileJson,
    Code, ChevronLeft, ChevronRight, Hash, Eye,
    Copy, ExternalLink, Filter, PlusCircle
} from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Card } from '../../../components/ui/card';
import { Badge } from '../../../components/ui/badge';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from "../../../components/ui/dialog";
import { toast } from 'react-toastify';

const ExplorerSection = ({
    explorerCollection,
    setExplorerCollection,
    explorerSearch,
    setExplorerSearch,
    explorerLoading,
    explorerData,
    fetchExplorerData,
    pageIndex,
    hasMore
}) => {
    const [selectedDoc, setSelectedDoc] = useState(null);
    const [showCustomInput, setShowCustomInput] = useState(false);
    const [customCollection, setCustomCollection] = useState('');

    const collections = [
        'Users', 'Companies', 'jobs', 'applications', 'notifications',
        'posts', 'events', 'contributions', 'ads', 'newsletter',
        'contactResponses', 'feedback', 'blogPosts', 'event_participants'
    ];

    // Dynamically determine headers from data
    const headers = useMemo(() => {
        if (!explorerData || explorerData.length === 0) return ['id'];

        const allKeys = new Set(['id']);
        explorerData.slice(0, 5).forEach(doc => {
            Object.keys(doc).forEach(key => {
                // Ignore complex nested objects for table columns
                if (typeof doc[key] !== 'object' || doc[key] instanceof Date || (doc[key] && doc[key].toDate)) {
                    allKeys.add(key);
                }
            });
        });

        // Return first 6 relevant keys
        return Array.from(allKeys).slice(0, 6);
    }, [explorerData]);

    const formatValue = (val) => {
        if (val === null || val === undefined) return '-';
        if (typeof val === 'boolean') return val ? 'Yes' : 'No';
        if (val && val.toDate) return val.toDate().toLocaleDateString();
        if (val instanceof Date) return val.toLocaleDateString();
        if (typeof val === 'object') return '{...}';
        const str = String(val);
        return str.length > 50 ? str.substring(0, 47) + '...' : str;
    };

    const handleCustomSubmit = (e) => {
        e.preventDefault();
        if (customCollection.trim()) {
            setExplorerCollection(customCollection.trim());
            setShowCustomInput(false);
            setCustomCollection('');
        }
    };

    return (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-8">
            <Card className="p-8 bg-card/40 backdrop-blur-xl border-border/40 rounded-[2.5rem] shadow-2xl overflow-hidden">
                {/* Header Controls */}
                <div className="flex flex-col xl:flex-row justify-between items-start xl:items-center gap-8 mb-10">
                    <div className="flex items-center gap-5">
                        <div className="w-16 h-16 rounded-[1.25rem] bg-indigo-500/10 flex items-center justify-center text-indigo-500 shadow-inner group">
                            <Database size={32} className="group-hover:scale-110 transition-transform" />
                        </div>
                        <div>
                            <h3 className="text-3xl font-black text-foreground tracking-tight">Database Explorer</h3>
                            <div className="flex items-center gap-2 mt-1">
                                <Badge variant="secondary" className="bg-primary/10 text-primary border-none font-bold text-[10px] uppercase tracking-tighter">
                                    {explorerCollection}
                                </Badge>
                                <span className="text-[10px] font-bold text-muted-foreground/60 uppercase tracking-widest leading-none">
                                    • {explorerData.length} records in view
                                </span>
                            </div>
                        </div>
                    </div>

                    <div className="flex flex-col lg:flex-row gap-4 w-full xl:w-auto flex-1 xl:max-w-4xl">
                        {/* Collection Selector */}
                        <div className="flex gap-2 flex-1 lg:max-w-[300px]">
                            {!showCustomInput ? (
                                <div className="w-full md:w-72 relative">
                                    <Database className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground" size={18} />
                                    <select
                                        value={explorerCollection}
                                        onChange={e => {
                                            if (e.target.value === 'custom') setShowCustomInput(true);
                                            else setExplorerCollection(e.target.value);
                                        }}
                                        style={{ backgroundColor: 'var(--bg-sidebar)', color: 'var(--text-main)' }}
                                        className="w-full h-[55px] pl-12 pr-10 rounded-2xl border border-border/40 font-black text-xs uppercase tracking-widest outline-none focus:ring-2 focus:ring-primary/20 appearance-none cursor-pointer transition-all"
                                    >
                                        <optgroup label="Core Collections">
                                            {collections.map(c => (
                                                <option key={c} value={c}>{c}</option>
                                            ))}
                                        </optgroup>
                                        <option value="custom">+ Manual Input</option>
                                    </select>
                                </div>
                            ) : (
                                <form onSubmit={handleCustomSubmit} className="flex gap-2 w-full">
                                    <Input
                                        autoFocus
                                        placeholder="Collection name..."
                                        value={customCollection}
                                        onChange={e => setCustomCollection(e.target.value)}
                                        className="h-[55px] rounded-2xl bg-background/50 border-primary/20 text-foreground font-bold"
                                    />
                                    <Button type="submit" className="h-[55px] px-6 rounded-2xl font-bold uppercase tracking-widest text-[10px]">Go</Button>
                                    <Button
                                        type="button"
                                        variant="ghost"
                                        onClick={() => setShowCustomInput(false)}
                                        className="h-[55px] px-4 rounded-2xl text-muted-foreground"
                                    >✕</Button>
                                </form>
                            )}
                        </div>

                        {/* Search */}
                        <div className="relative flex-1">
                            <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground" size={18} />
                            <Input
                                placeholder={`Search in ${explorerCollection} records...`}
                                value={explorerSearch}
                                onChange={e => setExplorerSearch(e.target.value)}
                                style={{ backgroundColor: 'var(--bg-sidebar)', color: 'var(--text-main)' }}
                                className="h-[55px] pl-12 rounded-2xl border-border/40 font-bold tracking-tight text-sm"
                            />
                        </div>

                        {/* Refresh */}
                        <Button
                            onClick={() => fetchExplorerData()}
                            disabled={explorerLoading}
                            variant="outline"
                            className="h-[55px] px-6 rounded-2xl border-border/40 hover:bg-secondary/40"
                        >
                            <RefreshCw className={explorerLoading ? "animate-spin" : ""} size={18} />
                        </Button>
                    </div>
                </div>

                {/* Table Logic */}
                <div className="rounded-[2rem] border border-border/40 bg-background/30 overflow-hidden shadow-2xl relative">
                    {explorerLoading && (
                        <div className="absolute inset-0 bg-background/60 backdrop-blur-sm z-50 flex flex-col items-center justify-center gap-4">
                            <div className="w-12 h-12 rounded-full border-4 border-primary/20 border-t-primary animate-spin" />
                            <span className="text-[10px] font-black uppercase tracking-[0.2em] text-primary">Fetching Data</span>
                        </div>
                    )}

                    <div className="overflow-x-auto custom-scrollbar">
                        <table className="w-full border-collapse">
                            <thead>
                                <tr className="bg-secondary/50 border-b border-border/40">
                                    {headers.map(header => (
                                        <th key={header} className="px-8 py-5 text-left text-[11px] font-black uppercase tracking-widest text-muted-foreground/80 h-[100%]">
                                            <div className="flex items-center gap-2">
                                                {header === 'id' ? <Hash size={12} className="text-primary" /> : <Filter size={10} />}
                                                {header}
                                            </div>
                                        </th>
                                    ))}
                                    <th className="px-8 py-5 text-right text-[11px] font-black uppercase tracking-widest text-muted-foreground/80">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-border/10">
                                {explorerData
                                    .filter(d => JSON.stringify(d).toLowerCase().includes(explorerSearch.toLowerCase()))
                                    .map((doc, idx) => (
                                        <tr key={doc.id} className="group hover:bg-primary/[0.04] transition-all">
                                            {headers.map(header => (
                                                <td key={header} className="px-8 py-6">
                                                    {header === 'id' ? (
                                                        <div className="flex items-center gap-3">
                                                            <div className="w-8 h-8 rounded-lg bg-secondary/50 flex items-center justify-center text-[10px] font-black text-muted-foreground/40 shadow-inner">
                                                                {pageIndex * 10 + idx + 1}
                                                            </div>
                                                            <code className="text-[11px] font-bold text-primary/70 bg-primary/5 px-2 py-1 rounded-md border border-primary/10">
                                                                {doc.id.substring(0, 8)}...
                                                            </code>
                                                            <button
                                                                onClick={() => {
                                                                    navigator.clipboard.writeText(doc.id);
                                                                    toast.success('ID copied');
                                                                }}
                                                                className="opacity-0 group-hover:opacity-100 transition-opacity p-1.5 hover:bg-primary/10 rounded-md text-primary"
                                                            >
                                                                <Copy size={12} />
                                                            </button>
                                                        </div>
                                                    ) : (
                                                        <span className="text-sm font-medium text-foreground/80">
                                                            {formatValue(doc[header])}
                                                        </span>
                                                    )}
                                                </td>
                                            ))}
                                            <td className="px-8 py-6 text-right">
                                                <Dialog>
                                                    <DialogTrigger asChild>
                                                        <Button
                                                            variant="secondary"
                                                            size="sm"
                                                            className="h-10 px-4 rounded-xl bg-secondary/50 hover:bg-primary hover:text-white transition-all font-black uppercase tracking-tighter text-[10px]"
                                                            onClick={() => setSelectedDoc(doc)}
                                                        >
                                                            <Eye size={14} className="mr-2" /> Inspect
                                                        </Button>
                                                    </DialogTrigger>
                                                    <DialogContent className="max-w-4xl p-0 overflow-hidden border-none rounded-[2.5rem] bg-[#0d1117]">
                                                        <DialogHeader className="p-8 pb-4 flex flex-row items-center justify-between border-b border-white/5">
                                                            <div>
                                                                <DialogTitle className="text-2xl font-black text-white flex items-center gap-3 tracking-tighter">
                                                                    <FileJson className="text-primary" />
                                                                    Document Inspection
                                                                </DialogTitle>
                                                                <p className="text-xs text-muted-foreground font-bold uppercase tracking-widest mt-1">ID: {selectedDoc?.id}</p>
                                                            </div>
                                                            <div className="flex gap-2">
                                                                <Button
                                                                    size="sm"
                                                                    variant="ghost"
                                                                    className="h-10 rounded-xl bg-white/5 border border-white/10 text-white/60 hover:text-white"
                                                                    onClick={() => {
                                                                        navigator.clipboard.writeText(JSON.stringify(selectedDoc, null, 2));
                                                                        toast.success('JSON Copied');
                                                                    }}
                                                                >
                                                                    <Copy size={14} className="mr-2" /> Copy JSON
                                                                </Button>
                                                            </div>
                                                        </DialogHeader>
                                                        <div className="p-8">
                                                            <pre className="p-8 rounded-[2rem] bg-[#010409] text-[#e6edf3] font-mono text-xs overflow-auto max-h-[60vh] custom-scrollbar border border-white/5 leading-relaxed shadow-2xl">
                                                                {JSON.stringify(selectedDoc, null, 2)}
                                                            </pre>
                                                        </div>
                                                    </DialogContent>
                                                </Dialog>
                                            </td>
                                        </tr>
                                    ))}
                            </tbody>
                        </table>

                        {explorerData.length === 0 && !explorerLoading && (
                            <div className="py-32 text-center">
                                <FileJson size={64} className="mx-auto mb-6 text-muted-foreground/10" />
                                <h4 className="text-xl font-black text-foreground/40 tracking-tight">Empty Collection</h4>
                                <p className="text-xs font-bold text-muted-foreground/30 uppercase tracking-widest mt-2 px-12 leading-relaxed">
                                    No documents found in "{explorerCollection}". <br />
                                    Check for typos or try syncing another collection.
                                </p>
                            </div>
                        )}
                    </div>

                    {/* Footer / Pagination */}
                    <div className="px-8 py-6 bg-secondary/30 border-t border-border/20 flex flex-col sm:flex-row items-center justify-between gap-6">
                        <div className="flex items-center gap-6">
                            <div className="flex items-center gap-2">
                                <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
                                <span className="text-[10px] font-black uppercase tracking-widest text-muted-foreground italic">Sync Mode: Live</span>
                            </div>
                            <div className="text-[11px] font-bold text-muted-foreground/60 tracking-tight">
                                Page <span className="text-foreground">{pageIndex + 1}</span> of many
                            </div>
                        </div>

                        <div className="flex items-center gap-3">
                            <Button
                                variant="outline"
                                size="sm"
                                className="h-11 px-6 rounded-2xl border-border/40 font-black uppercase tracking-widest text-[10px] bg-background/50 disabled:opacity-30"
                                onClick={() => fetchExplorerData('prev')}
                                disabled={pageIndex === 0 || explorerLoading}
                            >
                                <ChevronLeft size={16} className="mr-2" /> Prev
                            </Button>
                            <Button
                                variant="outline"
                                size="sm"
                                className="h-11 px-6 rounded-2xl border-border/40 font-black uppercase tracking-widest text-[10px] bg-background/50 disabled:opacity-30"
                                onClick={() => fetchExplorerData('next')}
                                disabled={!hasMore || explorerLoading}
                            >
                                Next <ChevronRight size={16} className="ml-2" />
                            </Button>
                        </div>
                    </div>
                </div>
            </Card>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Card className="p-8 bg-amber-500/5 border-amber-500/10 border rounded-[2rem] flex items-center gap-6 group hover:bg-amber-500/10 transition-colors">
                    <div className="w-14 h-14 rounded-2xl bg-amber-500/10 flex items-center justify-center text-amber-500 shadow-inner">
                        <PlusCircle size={28} />
                    </div>
                    <div>
                        <h4 className="font-black text-foreground tracking-tight">Manual Control</h4>
                        <p className="text-[11px] font-medium text-muted-foreground/70 leading-relaxed mt-1">
                            You can directly target any sub-collection or specific document by entering the path in the custom field above.
                        </p>
                    </div>
                </Card>

                <Card className="p-8 bg-blue-500/5 border-blue-500/10 border rounded-[2rem] flex items-center gap-6 group hover:bg-blue-500/10 transition-colors">
                    <div className="w-14 h-14 rounded-2xl bg-blue-500/10 flex items-center justify-center text-blue-500 shadow-inner">
                        <Code size={28} />
                    </div>
                    <div>
                        <h4 className="font-black text-foreground tracking-tight">Developer Mode</h4>
                        <p className="text-[11px] font-medium text-muted-foreground/70 leading-relaxed mt-1">
                            JSON inspection provides raw Firestore field types like GeoPoints, References, and Timestamps.
                        </p>
                    </div>
                </Card>
            </div>
        </motion.div >
    );
};

export default ExplorerSection;
